import React, { useEffect } from 'react';

import { Box } from '@mui/material';

import { SCOUT_BLUE } from '../../../../ThemeContext/constants';
import { classes } from '../styles';

import type { TableDensity } from '../types';

export interface Note {
  id: number;
  subject?: string;
  body: string;
  noted_by?: string;
}

interface NotesWithSubjectLineProps {
  notes: Note[];
  density?: TableDensity;
  onViewAll?: () => void;
}

const buttonHoverStyles = {
  '&:hover': {
    backgroundColor: 'rgba(8, 77, 109, 0.08)',
    textDecoration: 'underline'
  }
};

const NotesWithSubjectLine = ({
  notes,
  density = 'Default',
  onViewAll
}: NotesWithSubjectLineProps) => {
  const [notesExpanded, setNotesExpanded] = React.useState<boolean>(false);
  const [hasOverflow, setHasOverflow] = React.useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setHasOverflow(containerRef.current.scrollHeight > containerRef.current.clientHeight);
    }
  }, [containerRef, notes, density]);

  const handleToggleExpand = () => {
    setNotesExpanded(!notesExpanded);
  };

  // Ultra Compact: Show just "Show All" button with no preview, left-aligned, expanding on click
  if (density === 'Ultra Compact') {
    if (notesExpanded) {
      return (
        <Box>
          <Box
            ref={containerRef}
            sx={{
              ...classes.noteContainer,
              maxHeight: 'unset'
            }}
          >
            {notes?.map((note) => (
              <Box key={note.id}>
                {note.subject ? <Box sx={classes.noteSubject}>{note.subject}</Box> : null}
                <Box>{note.body}</Box>
                {note.noted_by ? <Box sx={classes.noteUser}>-{note.noted_by}</Box> : null}
              </Box>
            ))}
          </Box>
          <Box
            onClick={handleToggleExpand}
            sx={{
              color: SCOUT_BLUE,
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'Source Sans Pro, sans-serif',
              ...buttonHoverStyles
            }}
          >
            Show less ({notes.length})
          </Box>
        </Box>
      );
    }

    return (
      <Box
        onClick={handleToggleExpand}
        sx={{
          color: SCOUT_BLUE,
          cursor: 'pointer',
          fontSize: '14px',
          fontFamily: 'Source Sans Pro, sans-serif',
          ...buttonHoverStyles
        }}
      >
        Show All ({notes.length})
      </Box>
    );
  }

  // Compact: Preview of title (if exists) or body with tight spacing
  if (density === 'Compact') {
    if (notesExpanded) {
      return (
        <Box>
          <Box
            ref={containerRef}
            sx={{
              ...classes.noteContainer,
              maxHeight: 'unset'
            }}
          >
            {notes?.map((note) => (
              <Box key={note.id}>
                {!!note.subject && <Box sx={classes.noteSubject}>{note.subject}</Box>}
                <Box>{note.body}</Box>
                {!!note.noted_by && <Box sx={classes.noteUser}>-{note.noted_by}</Box>}
              </Box>
            ))}
          </Box>
          <Box
            onClick={handleToggleExpand}
            sx={{
              ...classes.notesExpandContract,
              fontSize: '13px',
              fontFamily: 'Source Sans Pro, sans-serif',
              ...buttonHoverStyles
            }}
          >
            Show less ({notes.length})
          </Box>
        </Box>
      );
    }

    const firstNote = notes[0];
    const previewText = firstNote?.subject || firstNote?.body;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {previewText ? (
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '14px',
              lineHeight: 1.3,
              fontFamily: 'Source Sans Pro, sans-serif',
              color: '#333'
            }}
          >
            {previewText}
          </Box>
        ) : null}
        <Box
          onClick={handleToggleExpand}
          sx={{
            ...classes.notesExpandContract,
            fontSize: '13px',
            fontFamily: 'Source Sans Pro, sans-serif',
            ...buttonHoverStyles
          }}
        >
          Show all ({notes.length})
        </Box>
      </Box>
    );
  }

  // Default: 3 lines max (~72px, assuming ~24px per line), with show all inset
  if (!notesExpanded && hasOverflow) {
    return (
      <Box sx={{ position: 'relative', maxHeight: '72px', overflow: 'hidden' }}>
        <Box
          ref={containerRef}
          sx={{
            ...classes.noteContainer,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            paddingBottom: '24px'
          }}
        >
          {notes?.map((note) => (
            <Box key={note.id}>
              {!!note.subject && <Box sx={classes.noteSubject}>{note.subject}</Box>}
              <Box>{note.body}</Box>
              {!!note.noted_by && <Box sx={classes.noteUser}>-{note.noted_by}</Box>}
            </Box>
          ))}
        </Box>
        <Box
          onClick={handleToggleExpand}
          sx={{
            ...classes.notesExpandContract,
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            cursor: 'pointer',
            background: 'linear-gradient(to bottom, transparent, white)',
            pt: 1,
            ...buttonHoverStyles
          }}
        >
          Show all ({notes.length})
        </Box>
      </Box>
    );
  }

  const containerStyles = notesExpanded
    ? { ...classes.noteContainer, maxHeight: 'unset' }
    : classes.noteContainer;
  // Default expanded or no overflow
  return (
    <Box>
      <Box ref={containerRef} sx={containerStyles}>
        {notes?.map((note) => (
          <Box key={note.id}>
            {!!note.subject && <Box sx={classes.noteSubject}>{note.subject}</Box>}
            <Box>{note.body}</Box>
            {!!note.noted_by && <Box sx={classes.noteUser}>-{note.noted_by}</Box>}
          </Box>
        ))}
      </Box>
      {notesExpanded ? (
        <Box
          onClick={handleToggleExpand}
          sx={{ ...classes.notesExpandContract, ...buttonHoverStyles }}
        >
          Show less
        </Box>
      ) : null}
    </Box>
  );
};

export default NotesWithSubjectLine;
